/**
 * Approval policy hooks - Settings > Claims > Approvals.
 *
 * The backend reads the policy from the live workflow rows and writes
 * through the platform policy writer; nothing here is tenant-specific.
 * Currency and strategy options are served by the API (tenant data /
 * backend labels), never hardcoded in the frontend.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

export interface ApprovalApprover {
  kind: 'role' | 'person'
  value: string
  label?: string
}

export interface ApprovalPolicyBand {
  currency: string
  amount: number
  approver: ApprovalApprover | null
}

export interface ApprovalPolicyOption {
  value: string
  label: string
}

export interface ApprovalPolicyResponse {
  configured: boolean
  enabled: boolean
  workflow_key: string
  bands: ApprovalPolicyBand[]
  strategy: string
  notify: boolean
  /** Lock the approved amount once decided; correct it via Revise Decision. */
  lock_decision: boolean
  currency_options: ApprovalPolicyOption[]
  strategy_options: ApprovalPolicyOption[]
  pending_count: number
  warnings: string[]
}

export interface ApprovalPolicyWritePayload {
  enabled: boolean
  bands: {
    currency: string
    amount: number
    approver: { kind: 'role' | 'person'; value: string } | null
  }[]
  strategy: string
  notify: boolean
  lock_decision: boolean
}

export function useApprovalPolicy(options: { enabled?: boolean } = {}) {
  return useQuery<ApprovalPolicyResponse>({
    queryKey: ['approval-policy'],
    queryFn: () => api.get('/claims/approval-policy/').then((r) => r.data),
    staleTime: 10_000,
    enabled: options.enabled ?? true,
  })
}

export function useUpdateApprovalPolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: ApprovalPolicyWritePayload) =>
      api
        .put('/claims/approval-policy/', data)
        .then((r) => r.data as ApprovalPolicyResponse),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-policy'] })
    },
  })
}

// ── LOA ladders (WPS-165) ─────────────────────────────────

/** Which levels sign: every level up to the amount, or only the one it lands in. */
export type ApprovalOrder = 'cumulative' | 'band'

export interface ApprovalLadderTier {
  order: number
  role_label: string
  /** The role this level routes to, when it names one. */
  approver_role: string
  approver_name: string
  min_amount: string | number | null
  max_amount: string | number | null
  band_label: string
}

export interface ApprovalLadderDimension {
  key: string
  label: string
  amount_label: string
  ladders: { matrix_key: string; tiers: ApprovalLadderTier[] }[]
}

export interface ApprovalLadderWorkflow {
  workflow_key: string
  label: string
  sign_off_mode: ApprovalOrder
  default_sign_off_mode: ApprovalOrder
  dimensions: ApprovalLadderDimension[]
}

export interface ApprovalLaddersResponse {
  results: ApprovalLadderWorkflow[]
  can_edit: boolean
}

export function useApprovalLadders() {
  return useQuery<ApprovalLaddersResponse>({
    queryKey: ['approval-ladders'],
    queryFn: () => api.get('/claims/approval-ladders/').then((r) => r.data),
    staleTime: 10_000,
  })
}

export function useUpdateApprovalOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { workflowKey: string; signOffMode: ApprovalOrder }) =>
      api
        .put(`/claims/approval-ladders/${data.workflowKey}/`, {
          sign_off_mode: data.signOffMode,
        })
        .then((r) => r.data as ApprovalLadderWorkflow),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-ladders'] })
    },
  })
}
